import { getUuid } from '@sibe/utilities/common';
import { HsDefaultMaterial } from '../../graphic-render-new/materials/default-material.class';
import type { HsMaterial } from '../../graphic-render-new/materials/material.class';
import { HsPointsMaterial } from '../../graphic-render-new/materials/points-material.class';
import { HsGraphicLines } from '../../graphic-render-new/objects/graphic-lines.class';
import type { HsGraphicObject } from '../../graphic-render-new/objects/graphic-object.class';
import { HsGraphicPoints } from '../../graphic-render-new/objects/graphic-points.class';
import type { HsGraphics } from '../../graphic-render-new/graphics.class';
import type { HsDefaultShader } from '../../graphic-render-new/shaders/classes/default-shader.class';
import { HsShaderData } from '../../graphic-render-new/shaders/classes/shader-data.class';
import { HsShaderObjectType } from '../../graphic-render-new/shaders/enums/shader-object-type.enum';
import { HsRegularGeometryData } from '../../tools-new/geometry-utils/regular-geometry-data.class';
import { hsMakeGeometry } from '../../tools-new/geometry-utils/make-geometry.function';
import type { HsRegularGeometry } from '../../graphic-engine-new/geometry/regular-geometry.class';

/** Where overlays are drawn: a viewer's context, graphics and queue. */
export interface HsViewerOverlayTarget {
  gl: WebGL2RenderingContext;
  graphics: HsGraphics;
  queue: { addObject(object: HsGraphicObject): void; removeObject(object: HsGraphicObject): void };
}

/**
 * Host-drawn objects over a scene, such as measurement lines, marker points and debug boxes, in named groups
 * a host replaces or clears as a whole. Line colors and the point material are compiled once and reused.
 */
export class HsViewerOverlays {
  private readonly groups: Map<string, HsGraphicObject[]> = new Map();
  private readonly lineMaterials: Map<string, HsMaterial> = new Map();
  private pointsMaterial: HsPointsMaterial<HsDefaultShader> | null = null;

  public constructor(private readonly target: HsViewerOverlayTarget) {}

  /**
   * Adds line segments (consecutive point pairs, xyz each) to `group` as one object named `name`; returns
   * it, or null when there is nothing to draw.
   */
  public addLines(
    group: string,
    name: string,
    vertices: readonly number[],
    color: readonly [number, number, number]
  ): HsGraphicLines | null {
    if (vertices.length < 6) {
      return null;
    }
    const geometry: HsRegularGeometry | null = hsMakeGeometry(
      this.target.graphics,
      new HsRegularGeometryData([...vertices], new Array<number>(vertices.length).fill(0))
    );
    if (geometry === null) {
      return null;
    }
    const lines: HsGraphicLines = new HsGraphicLines(getUuid(), name, geometry, this.lineMaterial(color));
    this.add(group, lines);
    return lines;
  }

  /** Adds points (xyz each, with an rgb color each) of `size` pixels to `group` as one object named `name`. */
  public addPoints(
    group: string,
    name: string,
    vertices: readonly number[],
    colors: readonly number[],
    size: number
  ): HsGraphicPoints | null {
    if (vertices.length < 3) {
      return null;
    }
    const geometry: HsRegularGeometry | null = hsMakeGeometry(
      this.target.graphics,
      new HsRegularGeometryData([...vertices], [...colors])
    );
    if (geometry === null) {
      return null;
    }
    const points: HsGraphicPoints = new HsGraphicPoints(getUuid(), name, geometry, this.pointMaterial());
    points.setPointsSize(this.target.gl, size);
    this.add(group, points);
    return points;
  }

  /** Adds an object the host built; the group owns it and deletes its geometry when cleared. */
  public add(group: string, object: HsGraphicObject): void {
    object.updateBoundingSphere();
    this.target.queue.addObject(object);
    const objects: HsGraphicObject[] | undefined = this.groups.get(group);
    if (objects === undefined) {
      this.groups.set(group, [object]);
    } else {
      objects.push(object);
    }
  }

  /** The objects of `group`, or of every group. */
  public objects(group?: string): readonly HsGraphicObject[] {
    return group === undefined ? [...this.groups.values()].flat() : this.groups.get(group) ?? [];
  }

  /** Removes the objects of `group`, or of every group, and deletes their geometry. */
  public clear(group?: string): void {
    const names: string[] = group === undefined ? [...this.groups.keys()] : [group];
    for (const name of names) {
      for (const object of this.groups.get(name) ?? []) {
        this.target.queue.removeObject(object);
        if (!object.geometry.isDeleted) {
          this.target.graphics.deleteBuffer(object.geometry.buffer);
          object.geometry.isDeleted = true;
        }
        object.isDeleted = true;
      }
      this.groups.delete(name);
    }
  }

  /** Clears every group and deletes the overlay shader programs. */
  public dispose(): void {
    this.clear();
    const materials: HsMaterial[] = [...this.lineMaterials.values()];
    if (this.pointsMaterial !== null) {
      materials.push(this.pointsMaterial);
    }
    materials.forEach((material: HsMaterial) => material.getShader().deleteProgram(this.target.gl));
    this.lineMaterials.clear();
    this.pointsMaterial = null;
  }

  private lineMaterial(color: readonly [number, number, number]): HsMaterial {
    const key: string = color.join(',');
    let material: HsMaterial | undefined = this.lineMaterials.get(key);
    if (material === undefined) {
      const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
      shaderData.addColor().addDepth();
      material = new HsDefaultMaterial(this.target.gl, shaderData.makeShader());
      material.color.set(color[0], color[1], color[2]);
      this.lineMaterials.set(key, material);
    }
    return material;
  }

  private pointMaterial(): HsPointsMaterial<HsDefaultShader> {
    if (this.pointsMaterial === null) {
      const shaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
      shaderData.addColor().addDepth().addPoints();
      this.pointsMaterial = new HsPointsMaterial(this.target.gl, shaderData.makeShader());
    }
    return this.pointsMaterial;
  }
}
